import type { EvaluatedElement } from "@vera/rules";
import type { ApsViewable } from "../stores/accModelStore";
import type { ElementProperties } from "../stores/propertiesStore";
import {
  ensureAccDocumentViewable,
  pick3DViewableId,
  waitForGeometryLoaded,
} from "./apsDocumentViewable";
import {
  buildLaCountyEvaluatedElements,
  type RawViewerElement,
} from "./casitaElementMapping";

const LA_CATEGORIES = new Set([
  "Revit Doors",
  "Revit Windows",
  "Revit Runs",
  "Revit Rooms",
]);

interface BulkPropResult {
  dbId: number;
  name?: string;
  properties?: Array<{
    displayName: string;
    displayCategory: string;
    displayValue: string | number;
  }>;
}

function categoryFromProps(
  props: BulkPropResult["properties"]
): string | undefined {
  if (!props) return undefined;
  const cat = props.find(
    p =>
      p.displayName === "Category" &&
      (p.displayCategory === "Identity Data" ||
        p.displayCategory === "__category__")
  );
  if (cat && typeof cat.displayValue === "string") return cat.displayValue;
  return undefined;
}

function toRawElement(row: BulkPropResult): RawViewerElement | null {
  const properties = (row.properties ?? []).map(p => ({
    displayName: p.displayName,
    displayCategory: p.displayCategory,
    displayValue: String(p.displayValue ?? ""),
  }));
  const category = categoryFromProps(row.properties);
  if (!category || !LA_CATEGORIES.has(category)) return null;
  return {
    dbId: row.dbId,
    name: row.name,
    category,
    properties,
  };
}

function collectDbIds(model: Autodesk.Viewing.Model): Promise<number[]> {
  return new Promise((resolve, reject) => {
    model.getObjectTree(tree => {
      if (!tree) {
        reject(new Error("Forge Viewer returned no instance tree."));
        return;
      }
      const ids: number[] = [];
      tree.enumNodeChildren(
        tree.getRootId(),
        dbId => {
          ids.push(dbId);
        },
        true
      );
      resolve(ids);
    });
  });
}

function getBulkProperties(
  model: Autodesk.Viewing.Model,
  dbIds: number[],
  onChunk?: (done: number, total: number) => void
): Promise<BulkPropResult[]> {
  return new Promise((resolve, reject) => {
    const CHUNK = 400;
    const results: BulkPropResult[] = [];
    let offset = 0;

    const next = () => {
      if (offset >= dbIds.length) {
        onChunk?.(dbIds.length, dbIds.length);
        resolve(results);
        return;
      }
      const slice = dbIds.slice(offset, offset + CHUNK);
      offset += CHUNK;
      model.getBulkProperties(
        slice,
        { propFilter: undefined },
        (rows: BulkPropResult[]) => {
          results.push(...rows);
          onChunk?.(Math.min(offset, dbIds.length), dbIds.length);
          next();
        },
        (err: unknown) => {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      );
    };
    next();
  });
}

export interface LiveViewerExtraction {
  evaluatedElements: EvaluatedElement[];
  propertiesByDbId: Map<number, ElementProperties>;
}

/** Pull Revit properties from an APS-hosted model and map to LA County facts. */
export async function extractLiveFromViewer(
  viewer: Autodesk.Viewing.GuiViewer3D,
  onProgress?: (percent: number, message: string) => void
): Promise<LiveViewerExtraction> {
  const model = viewer.model;
  if (!model) {
    throw new Error("Viewer has no loaded model.");
  }

  onProgress?.(74, "Scanning model elements…");
  const dbIds = await collectDbIds(model);
  console.info(`[Vera:ACC] Instance tree: ${dbIds.length} nodes`);
  onProgress?.(76, `Reading properties (${dbIds.length} elements)…`);
  const bulk = await getBulkProperties(model, dbIds, (done, total) => {
    const pct = 76 + Math.round((done / total) * 14);
    onProgress?.(pct, `Reading Revit properties… (${done}/${total})`);
  });

  const categoryHistogram: Record<string, number> = {};
  for (const row of bulk) {
    const cat = categoryFromProps(row.properties);
    if (cat) categoryHistogram[cat] = (categoryHistogram[cat] ?? 0) + 1;
  }
  const laRawCounts: Record<string, number> = {};
  for (const cat of LA_CATEGORIES) {
    laRawCounts[cat] = categoryHistogram[cat] ?? 0;
  }
  console.info(
    "[Vera:ACC] Revit categories in 3D model (LA-relevant):",
    laRawCounts
  );
  if (Object.values(laRawCounts).every(n => n === 0)) {
    const top = Object.entries(categoryHistogram)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");
    console.warn(
      "[Vera:ACC] No Revit Doors/Windows/Rooms/Runs in this viewable — top categories:",
      top || "(none)"
    );
  }

  const rawByDbId: Record<string, RawViewerElement> = {};
  const propertiesByDbId = new Map<number, ElementProperties>();

  for (const row of bulk) {
    const raw = toRawElement(row);
    if (!raw) continue;
    rawByDbId[String(raw.dbId)] = raw;
    propertiesByDbId.set(raw.dbId, {
      dbId: raw.dbId,
      name: raw.name,
      category: raw.category,
      properties: raw.properties,
    });
  }

  const evaluatedElements = buildLaCountyEvaluatedElements(rawByDbId);
  const byOntology: Record<string, number> = {};
  for (const el of evaluatedElements) {
    byOntology[el.ontologyType] = (byOntology[el.ontologyType] ?? 0) + 1;
  }
  console.info(
    `[Vera:ACC] Mapped ${evaluatedElements.length} evaluable elements (${Object.keys(rawByDbId).length} raw Revit objects)`,
    byOntology
  );
  onProgress?.(92, "Running building-code rules…");
  return { evaluatedElements, propertiesByDbId };
}

/**
 * Extract from the 3D viewable so all doors/windows/rooms/stairs are present.
 * A 2D sheet only exposes elements visible on that sheet (~27 vs ~75 for Casita).
 */
export async function extractLiveFromAccDocument(
  viewer: Autodesk.Viewing.GuiViewer3D,
  doc: Autodesk.Viewing.Document,
  viewables: ApsViewable[],
  restoreViewableId: string | null,
  onProgress?: (percent: number, message: string) => void
): Promise<LiveViewerExtraction> {
  const threeDId = pick3DViewableId(viewables);
  const restoreId = restoreViewableId ?? threeDId;

  if (threeDId) {
    onProgress?.(74, "Loading full building model for property extraction…");
    const picked = viewables.find(v => v.id === threeDId);
    const node = await ensureAccDocumentViewable(doc, threeDId);
    if (!node) {
      throw new Error(
        "No 3D viewable found for full-model property extraction."
      );
    }
    console.info(
      "[Vera:ACC] Switching to 3D viewable for extraction:",
      picked?.name ?? threeDId
    );
    await viewer.loadDocumentNode(doc, node);
    await waitForGeometryLoaded(viewer);
  }

  const result = await extractLiveFromViewer(viewer, onProgress);

  if (restoreId && threeDId && restoreId !== threeDId) {
    console.info(
      "[Vera:ACC] Restoring sheet view after extraction:",
      restoreId
    );
    const restoreNode = await ensureAccDocumentViewable(doc, restoreId);
    if (restoreNode) {
      await viewer.loadDocumentNode(doc, restoreNode);
      await waitForGeometryLoaded(viewer);
    }
  }

  return result;
}
